import { NavLink } from 'react-router'

import type { NavItem } from '../lib/nav'
import { Icon } from './ui'

interface SideNavProps {
  /** The destinations to render. See src/lib/nav.ts. */
  items: readonly NavItem[]
  /**
   * The `<nav>`'s accessible name, and it is REQUIRED for a reason.
   *
   * From Sprint 1.9 there are two navigations on screen at once for an
   * administrator. Two landmarks with the same name — or one with none — are
   * indistinguishable to a screen-reader user moving between landmarks, and they
   * are equally indistinguishable to a test: `getByRole('navigation', { name })`
   * throws on multiple matches, and Playwright's strict mode fails. An optional
   * label would have made the product nav the unnamed one by default, which is
   * the worse half of that.
   */
  label: string
  /** Resolved badge counts, keyed by NavItem.badge. */
  counts?: { pendingCount?: number }
  /** Called after a destination is chosen — the drawer uses it to close. */
  onNavigate?: () => void
}

/**
 * Consecutive runs of items that share a `section`.
 *
 * CONSECUTIVE, not keyed by name: grouping by value would let a list whose
 * sections interleave render one group out of source order, and the order in
 * `nav.ts` is the order on screen. An item with no `section` starts a group
 * with no label — which is what `ADMIN_NAV` is.
 */
function group(items: readonly NavItem[]): { section?: string; items: NavItem[] }[] {
  const groups: { section?: string; items: NavItem[] }[] = []
  for (const item of items) {
    const last = groups.at(-1)
    if (last && last.section === item.section) last.items.push(item)
    else groups.push({ section: item.section, items: [item] })
  }
  return groups
}

/**
 * A navigation list. Rendered inside the desktop rail and inside the phone
 * drawer, from one definition — see src/lib/nav.ts.
 *
 * **One `<nav>` per call, however many groups.** `.nav__section` is a `<div>`
 * and not a heading: promoting it would put two more entries in the heading
 * outline of every screen, above the `<h1>`, and promoting the group to its own
 * `<nav>` would break the invariant `AppShell.test.tsx:78-83` pins — the
 * landmarks are exactly `['Main', 'Admin']`. A group label is a visual grouping
 * of links that already name themselves.
 */
export function SideNav({ items, label, counts, onNavigate }: SideNavProps) {
  return (
    <nav className="nav" aria-label={label}>
      {group(items).map((g) => (
        <div key={g.items[0].to}>
          {g.section && <div className="nav__section">{g.section}</div>}
          <ul className="nav__list">
            {g.items.map((item) => {
              const count = item.badge ? counts?.[item.badge] : undefined
              return (
                <li key={item.to}>
                  <NavLink
                    to={item.to}
                    className={({ isActive }) => `nav__link${isActive ? ' nav__link--active' : ''}`}
                    onClick={onNavigate}
                  >
                    {/* Decoration. `Icon` is `aria-hidden` and unsized, so the
                        link's accessible name stays the label alone -- "sun
                        Today" helps nobody. */}
                    {item.icon && <Icon name={item.icon} className="nav__icon" />}
                    <span className="nav__label">{item.label}</span>
                    {/* Decoration, like the icon: the link's name stays its label
                        ("Assistant", not "Assistant New"). */}
                    {item.tag === 'new' && (
                      <span className="nav__tag" aria-hidden="true">
                        New
                      </span>
                    )}
                    {count != null && count > 0 && (
                      <span className="nav__badge">
                        {count}
                        {/* The number alone reads as "Approval queue 4" out of
                            context; say what it counts. */}
                        <span className="visually-hidden"> waiting for approval</span>
                      </span>
                    )}
                  </NavLink>
                </li>
              )
            })}
          </ul>
        </div>
      ))}
    </nav>
  )
}
